import type {
  DataSchema,
  Integration,
  IntegrationAuthOption,
} from '@membranehq/sdk';
import { useCallback } from 'react';

interface UseIntegrationFieldsProps {
  integration: Integration | null;
  getSchema: (integrationData?: Integration | null) => DataSchema | null;
}

export const useIntegrationFields = ({
  integration,
  getSchema,
}: UseIntegrationFieldsProps) => {
  const findFieldProperty = useCallback(
    (
      key: string,
    ): {
      property?: {
        title?: string;
        label?: string;
        description?: string;
        type?: string | string[];
        format?: string;
      };
      authOption?: IntegrationAuthOption;
    } | null => {
      const authOptions = integration?.authOptions;
      if (authOptions) {
        for (const option of authOptions) {
          const optionSchema = option.ui?.schema;
          if (optionSchema?.properties?.[key]) {
            return { property: optionSchema.properties[key] };
          }
        }
        const authOption = authOptions.find((opt) => opt.key === key);
        if (authOption) {
          return { authOption };
        }
      }

      const schema = getSchema();
      const property = schema?.properties?.[key];
      return property ? { property } : null;
    },
    [integration, getSchema],
  );

  const getFieldLabel = useCallback(
    (key: string): string => {
      const fieldInfo = findFieldProperty(key);
      if (fieldInfo?.property) {
        return fieldInfo.property.title || fieldInfo.property.label || key;
      }
      if (fieldInfo?.authOption) {
        return fieldInfo.authOption.title || key;
      }
      return key;
    },
    [findFieldProperty],
  );

  const getFieldType = useCallback(
    (key: string): string => {
      const fieldInfo = findFieldProperty(key);
      if (fieldInfo?.property) {
        const type = Array.isArray(fieldInfo.property.type)
          ? fieldInfo.property.type[0]
          : fieldInfo.property.type || 'string';
        if (fieldInfo.property.format === 'password') {
          return 'password';
        }
        return type === 'string' ? 'text' : type;
      }
      if (fieldInfo?.authOption) {
        const optionSchema = fieldInfo.authOption.ui?.schema;
        if (optionSchema?.format === 'password') {
          return 'password';
        }
        return 'text';
      }
      return 'text';
    },
    [findFieldProperty],
  );

  const getFieldDescription = useCallback(
    (key: string): string | undefined => {
      const fieldInfo = findFieldProperty(key);
      if (fieldInfo?.property) {
        return fieldInfo.property.description;
      }
      if (fieldInfo?.authOption) {
        return fieldInfo.authOption.description;
      }
      return undefined;
    },
    [findFieldProperty],
  );

  const isFieldRequired = useCallback(
    (key: string): boolean => {
      const fieldInfo = findFieldProperty(key);
      if (fieldInfo?.property) {
        const schema = getSchema();
        return schema?.required?.includes(key) || false;
      }
      if (fieldInfo?.authOption) {
        const optionSchema = fieldInfo.authOption.ui?.schema;
        return optionSchema?.required?.includes(key) ?? false;
      }
      const schema = getSchema();
      return schema?.required?.includes(key) || false;
    },
    [findFieldProperty, getSchema],
  );

  return {
    getFieldLabel,
    getFieldType,
    getFieldDescription,
    isFieldRequired,
  };
};
